import dayjs from 'dayjs';
import { GeneralUser, UserRole } from '@/types';
import { format } from 'date-fns';

export type SubscriptionDerivedState = {
  trialActive: boolean;
  trialPreviouslyEnrolled: boolean;
  hasTrialHistory: boolean;
  trialEndsAt?: Date;
  trialDaysRemaining: number;
  trialTotalDays?: number;
  trialDaysDisplay: number;
  monthlyPrice: number;
  status?: string | boolean | undefined;
  nextBillingDate?: string | null;
  renewalDate?: string | null;
  interval?: string | null;
};

export const deriveSubscriptionState = (
  user?: Partial<GeneralUser> | null,
): SubscriptionDerivedState => {
  const now = dayjs();
  const trialEnd = user?.stripeSubscriptionTrialEnd
    ? dayjs(user.stripeSubscriptionTrialEnd)
    : null;
  // TCX-224: prefer the live (normalized) Stripe status fetched in `getProfile`
  // over the stored `stripeSubscriptionStatus`. The stored field lags a missed
  // `customer.subscription.updated` webhook until the daily reconciler heals it,
  // which is what left a converted trial showing "Trial Active" past the first
  // billing date. Fall back to the stored value when there's no live info
  // (e.g. canceled sub or Stripe unreachable).
  const status =
    user?.subscriptionInfo?.normalizedStatus ?? user?.stripeSubscriptionStatus;

  const trialActive =
    status === 'trialing' || (!!trialEnd && trialEnd.isAfter(now));

  const trialPreviouslyEnrolled =
    !!trialEnd && trialEnd.isBefore(now) && status !== 'trialing';

  const trialDaysRemaining =
    trialActive && trialEnd
      ? Math.max(Math.ceil(trialEnd.diff(now, 'day', true)), 0)
      : 0;

  const trialTotalDays =
    typeof user?.trialDays === 'number' ? user.trialDays : undefined;

  const hasTrialHistory =
    trialPreviouslyEnrolled || trialActive || !!trialTotalDays;

  const trialDaysDisplay = trialActive
    ? trialDaysRemaining || trialTotalDays || 0
    : 0;

  const monthlyPrice = user?.subscriptionInfo?.amount || 0;

  const nextBillingDate = user?.subscriptionInfo?.nextBillingDate
    ? format(user?.subscriptionInfo?.nextBillingDate, 'dd MMM, yyyy')
    : null;

  // TCX-659: 12-month commitment renewal/term-end date for the franchisor
  // "Renews on" row. Backend anchors it to the subscription start_date.
  // TCX-454: match the card's "Next billing date" format ('MMM d, yyyy') so the
  // two dates on the card don't render in different formats.
  const renewalDate = user?.subscriptionInfo?.termRenewalDate
    ? format(new Date(user.subscriptionInfo.termRenewalDate), 'MMM d, yyyy')
    : null;

  const interval = user?.subscriptionInfo?.interval;

  return {
    trialActive,
    trialPreviouslyEnrolled,
    hasTrialHistory,
    trialEndsAt: trialEnd?.toDate(),
    trialDaysRemaining,
    trialTotalDays,
    trialDaysDisplay,
    monthlyPrice,
    status,
    nextBillingDate,
    renewalDate,
    interval,
  };
};

// TCX-841: decompose a franchisor plan label into its two display parts — the
// plan `name` and the billing-cadence `suffix` — so they can be rendered with
// different visual weight (name bold, suffix lighter) instead of one flat
// string. The <PlanLabel> component consumes this for both the active-
// subscription card and the buy-screen plan tiles; keeping the format logic here
// as a pure function means it has one home and stays cheap to unit-test.
//
// `base` carries forward the TCX-454 safety: a Stripe price with a blank/missing
// nickname must never render blank, so it falls back to "Subscription" (the
// backend already collapses '' to null; the trim also guards a whitespace-only
// name). `suffix` is null for intervals other than month/year (Stripe permits
// day/week) and for a missing interval — omit it rather than invent copy.
export const getPlanLabelParts = (
  name?: string | null,
  interval?: string | null,
): { base: string; suffix: string | null } => {
  const base = name?.trim() || 'Subscription';
  if (interval === 'year') return { base, suffix: '(Billed Annually)' };
  if (interval === 'month') return { base, suffix: '(Billed Monthly)' };
  return { base, suffix: null };
};
